import { Globe2, Smartphone, Plug, ArrowUpRight } from "lucide-react";
import { WebVisual, MobileVisual, ApiVisual } from "@/components/sections/ServiceVisuals";

const services = [
  {
    id: "web",
    icon: Globe2,
    title: "Web Apps",
    desc: "Production-grade dashboards, SaaS platforms and marketing sites with 100/100 Lighthouse performance.",
    bullets: ["React / Next.js", "Edge-deployed", "SEO-ready"],
    Visual: WebVisual,
    tag: "Web Dev",
  },
  {
    id: "mobile",
    icon: Smartphone,
    title: "Mobile Apps",
    desc: "iOS & Android apps that feel native, ship weekly and scale to millions without rewrites.",
    bullets: ["React Native", "Offline-first", "Push & analytics"],
    Visual: MobileVisual,
    tag: "Mobile",
  },
  {
    id: "api",
    icon: Plug,
    title: "API Integrations",
    desc: "Stripe, Twilio, OpenAI, internal microservices — wired with retries, observability and zero-downtime.",
    bullets: ["REST & GraphQL", "Webhook reliability", "Type-safe SDKs"],
    Visual: ApiVisual,
    tag: "Backend",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-2xl reveal">
          <div className="text-xs font-mono-d uppercase tracking-[0.28em] text-[#0066ff] mb-3">
            / 02 — Capabilities
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.98] tracking-tight text-[#0a0e1a]">
            What we build, <span className="text-grad">end-to-end</span>.
          </h2>
          <p className="mt-5 text-[#475569] text-base sm:text-lg">
            Three focused practices. One senior team. Zero hand-offs.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-5 sm:gap-6">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const Visual = s.Visual;
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="reveal surface-strong card-lift rounded-2xl overflow-hidden"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="relative">
                  <Visual />
                  <div className="photo-tag">{s.tag}</div>
                  <div className="absolute bottom-3 left-3 z-[2]">
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-white shadow-[0_8px_18px_-6px_rgba(10,15,30,0.35)] border border-[#e6ecff]">
                      <Icon className="w-5 h-5 text-[#0066ff]" strokeWidth={2.4} />
                    </span>
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl font-bold tracking-tight text-[#0a0e1a]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-[#1f2937]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 inline-flex items-center gap-1 text-sm font-bold text-[#0066ff]">
                    Learn more <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
